import FontAwesome from 'react-native-vector-icons/FontAwesome';
import Ionicons from 'react-native-vector-icons/Ionicons';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { createStackNavigator } from '@react-navigation/stack';
import { useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Animated, Platform, StyleSheet, Text, View } from 'react-native';
import { SafeAreaProvider, useSafeAreaInsets } from 'react-native-safe-area-context';

import { ThemeProvider, useTheme } from './context/ThemeContext';
import { PlayerProvider } from './context/PlayerContext';
import { useAuth } from './context/AuthContext';
import { ChatStateProvider } from './context/ChatStateContext';
import { MissionsProvider } from './context/MissionsContext';
import { NotificationsProvider, useNotifications } from './context/NotificationsContext';
import { useUpdates } from './context/UpdatesContext';
import { UpdatesProvider } from './context/UpdatesContext';
import { StoryProgressProvider } from './context/StoryProgressContext';
import { getThemeColors } from './utils/themeStyles';
import { useResponsive } from './hooks/useResponsive';
import DesktopLayout from './components/DesktopLayout';
import GlobalLoadingIndicator from './components/GlobalLoadingIndicator';
import NotificationToast from './components/NotificationToast';

import MissionsScreen from './screens/MissionsScreen';
import ChatScreen from './screens/ChatScreen';
import DashboardScreen from './screens/DashboardScreen';
import NotificationsScreen from './screens/NotificationsScreen';
import ProfileScreen from './screens/ProfileScreen';
import CardDiscussionScreen from './screens/CardDiscussionScreen';
import CartScreen from './screens/CartScreen';
import ChapterDetailScreen from './screens/ChapterDetailScreen';
import MissionCardsScreen from './screens/MissionCardsScreen';
import MissionChatScreen from './screens/MissionChatScreen';
import OrderPipelineScreen from './screens/OrderPipelineScreen';
import QuizScreen from './screens/QuizScreen';
import SceneReaderScreen from './screens/SceneReaderScreen';
import TaskDetailScreen from './screens/TaskDetailScreen';
import CodeIdeScreen from './screens/CodeIdeScreen';
import UsChatScreen from './screens/UsChatScreen';
import CharacterChatScreen from './screens/CharacterChatScreen';
import MissionDetailScreen from './screens/MissionDetailScreen';
import UserStoryDetailScreen from './screens/UserStoryDetailScreen';
import LoginScreen from './screens/auth/LoginScreen';
import MyInformationScreen from './screens/MyInformationScreen';
import CompanyAdminScreen from './screens/company/CompanyAdminScreen';
import CompanyDetailScreen from './screens/company/CompanyDetailScreen';
import MyInvitationsScreen from './screens/company/MyInvitationsScreen';
import InviteScreen from './screens/company/InviteScreen';
import { captureInvite, getPendingInvite } from './navigation/pendingInvite';
import { useT } from './i18n/useT';
import { boot as bootUiLanguage } from './i18n/store';
import { uiLanguageFromUrl, type MessageKey } from './i18n';

const Tab = createBottomTabNavigator();
const Stack = createStackNavigator();
const AuthStack = createStackNavigator();
const PublicStack = createStackNavigator();

function TabNavigator() {
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const { unread } = useNotifications();
  //: The board's badge comes from the SAME counter as the desktop menu's — two sources for one
  //: number would disagree the day one is refreshed and not the other (CCT-348).
  const { board } = useUpdates();
  const insets = useSafeAreaInsets(); // marge basse système (Android nav bar / iOS home)

  return (
    <Tab.Navigator
      screenOptions={({ route }) => ({
        headerShown: false,
        tabBarActiveTintColor: colors.tabActive,
        tabBarInactiveTintColor: colors.tabInactive,
        tabBarStyle: {
          backgroundColor: colors.tabBackground,
          borderTopWidth: 1,
          borderTopColor: colors.border,
          paddingBottom: 10 + insets.bottom, // au-dessus de la barre de navigation système
          paddingTop: 5,
          height: 70 + insets.bottom,
          elevation: 0,
          shadowOpacity: 0,
        },
        tabBarLabelStyle: {
          fontSize: 12,
          fontWeight: '600',
          marginTop: 2,
        },
        tabBarIcon: ({ focused, color, size }) => {
          if (route.name === 'Missions') {
            return (
              <View style={styles.tabIconContainer}>
                <FontAwesome name="bullseye" size={size} color={color} />
              </View>
            );
          }

          let iconName: string;
          let badgeCount: number | undefined;

          if (route.name === 'Dashboard') {
            // ⚠️ THE BOARD WAS UNREACHABLE ON A PHONE (CCT-396). The tab bar held Missions, Chat,
            // Notifications and Account: the task board — the one screen that shows the work
            // across missions — had no entry at all, while the desktop menu has had one all along.
            iconName = focused ? 'grid' : 'grid-outline';
            badgeCount = board > 0 ? board : undefined;
          } else if (route.name === 'Chat') {
            iconName = focused ? 'chatbox' : 'chatbox-outline';
            // Pas de pastille : le `3` d'avant était une constante, pas un compte.
          } else if (route.name === 'Notifications') {
            iconName = focused ? 'notifications' : 'notifications-outline';
            badgeCount = unread > 0 ? unread : undefined;
          } else {
            iconName = focused ? 'person' : 'person-outline';
          }

          return (
            <View style={styles.tabIconContainer}>
              <Ionicons name={iconName} size={size} color={color} />
              {badgeCount !== undefined && (
                <View style={[styles.badge, { backgroundColor: colors.badgeBackground }]}>
                  <Text style={[styles.badgeText, { color: colors.badgeText }]}>{badgeCount}</Text>
                </View>
              )}
            </View>
          );
        },
        tabBarItemStyle: {
          borderRadius: 10,
          marginHorizontal: 2,
          marginVertical: 2,
          paddingHorizontal: 12,
          paddingVertical: 8,
          minHeight: 50,
          paddingBottom: 10,
          alignItems: 'center',
          justifyContent: 'center',
          flex: 1,
        },
        tabBarActiveBackgroundColor: colors.tabActiveBackground,
      })}
    >
      <Tab.Screen name="Missions" component={MissionsScreen} />
      <Tab.Screen name="Dashboard" component={DashboardScreen} />
      <Tab.Screen name="Chat" component={ChatScreen} />
      <Tab.Screen name="Notifications" component={NotificationsScreen} />
      <Tab.Screen name="Account" component={ProfileScreen} />
    </Tab.Navigator>
  );
}

function MainNavigator() {
  const { isDesktop } = useResponsive();
  const MainComponent = isDesktop ? DesktopLayout : TabNavigator;

  // Invitation en attente (deep-link) -> démarre sur l'écran de confirmation après auth.
  const initialMain = getPendingInvite() ? 'Invite' : 'Main';

  return (
    <Stack.Navigator initialRouteName={initialMain} screenOptions={{ headerShown: false }}>
      <Stack.Screen name="Main" component={MainComponent} />
      <Stack.Screen name="Invite" component={InviteScreen} initialParams={{ token: getPendingInvite() ?? undefined }} />
      <Stack.Screen name="CardDiscussion" component={CardDiscussionScreen} />
      <Stack.Screen name="Cart" component={CartScreen} options={{ presentation: "modal" }} />
      <Stack.Screen name="OrderPipeline" component={OrderPipelineScreen} options={{ presentation: "modal" }} />
      <Stack.Screen name="ChapterDetail" component={ChapterDetailScreen} />
      <Stack.Screen name="MissionCards" component={MissionCardsScreen} />
      <Stack.Screen name="UserStoryDetail" component={UserStoryDetailScreen} />
      <Stack.Screen name="MissionChat" component={MissionChatScreen} />
      <Stack.Screen name="Quiz" component={QuizScreen} />
      <Stack.Screen name="SceneReader" component={SceneReaderScreen} />
      <Stack.Screen name="TaskDetail" component={TaskDetailScreen} />
      {/* CodeController : l'IDE réel est CodeIdeScreen.web.tsx ; en natif, l'écran
          affiche « à réaliser sur poste de travail » (cf. CCT-73). */}
      <Stack.Screen name="CodeIde" component={CodeIdeScreen} />
      {/* Chat runtime personnage↔joueur (hint de blocage, refus de livraison) — CCT-74.
          Ouvert par le push du moteur ; distinct du chat SCRIPTÉ de la storyline. */}
      <Stack.Screen name="UsChat" component={UsChatScreen} />
      <Stack.Screen name="CharacterChat" component={CharacterChatScreen} />
      <Stack.Screen name="MissionDetail" component={MissionDetailScreen} />
      <Stack.Screen name="MyInformation" component={MyInformationScreen} />
      <Stack.Screen name="CompanyAdmin" component={CompanyAdminScreen} />
      <Stack.Screen name="CompanyDetail" component={CompanyDetailScreen} />
      <Stack.Screen name="MyInvitations" component={MyInvitationsScreen} />
    </Stack.Navigator>
  );
}

// Routes accessibles SANS authentification (le reste est protégé).
// ⚠️ `PublicMissions` et `Login` n'ont pas de path dans linking.ts → react-navigation utilise le
// NOM de route comme URL (`/PublicMissions`, `/Login`). Il faut donc lister `/publicmissions` ici,
// sinon un refresh sur la page publique (URL `/PublicMissions`) est vu comme un deep-link protégé
// → renvoi vers Login, puis `/login` (public) → PublicMissions… d'où l'alternance à chaque refresh.
// `/missiondetail` : même raison que `/publicmissions` ci-dessus — dans le stack PUBLIC, cet
// écran n'a pas de path (linking.ts le mappe sous `Main`), donc react-navigation écrit son NOM
// de route dans l'URL.
const PUBLIC_PATHS = ['/', '/publicmissions', '/missiondetail', '/cart', '/auth/callback',
                      '/logout/callback', '/login'];

// Chemins publics à PRÉFIXE (l'id varie) : la fiche d'une mission du catalogue est consultable
// sans compte — c'est la vitrine. En comparaison exacte, `/mission/3` était traité comme une URL
// protégée et renvoyait vers Login (CCT-253).
const PUBLIC_PREFIXES = ['/mission/'];

function isPublicPath(path: string): boolean {
  return PUBLIC_PATHS.includes(path) || PUBLIC_PREFIXES.some((p) => path.startsWith(p));
}

/** Id de la fiche mission à ouvrir : depuis l'URL (`/mission/5`), sinon la dernière consultée.
 *
 *  Le repli sert le RAFRAÎCHISSEMENT : dans le stack public, react-navigation réécrit l'URL en
 *  `/MissionDetail` (l'écran n'a pas de chemin de ce côté, cf. `linking.ts`), donc l'id n'est
 *  plus dans l'URL au moment du F5. `MissionDetailScreen` mémorise la mission ouverte.
 */
function missionIdToOpen(): number | undefined {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return undefined;
  const m = /^\/mission\/(\d+)/i.exec(window.location.pathname);
  if (m) return Number(m[1]);
  if (window.location.pathname.toLowerCase().replace(/\/+$/, '') !== '/missiondetail') return undefined;
  try {
    const memo = Number(sessionStorage.getItem('ccjaw.missionDetail'));
    return Number.isFinite(memo) && memo > 0 ? memo : undefined;
  } catch {
    return undefined;
  }
}

// Guard deep-link (web) : si un utilisateur déconnecté ouvre directement une URL
// protégée (ou si l'intercepteur 401 a mémorisé un returnTo), on démarre sur
// l'écran de connexion et on mémorise la page visée pour y revenir après login.
function computePublicInitialRoute(): 'PublicMissions' | 'Login' | 'Invite' | 'MissionDetail' {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return 'PublicMissions';
  // Lien d'invitation (/invite/<token> ou ?invite=) : écran de confirmation dédié
  // (avec CTA connexion/inscription), pas l'accueil ni un Login générique.
  if (captureInvite()) return 'Invite';
  // Normalise (casse + slash final) : '/Login' -> '/login', '/cart/' -> '/cart'.
  const path = (window.location.pathname.toLowerCase().replace(/\/+$/, '') || '/');
  // On force le Login UNIQUEMENT si le chemin lui-même est protégé (deep link
  // ou page protégée après un 401 — l'URL est alors encore la page protégée).
  if (!isPublicPath(path)) {
    try {
      sessionStorage.setItem('ccjaw.returnTo', window.location.pathname + window.location.search);
    } catch {
      /* ignore */
    }
    return 'Login';
  }
  // Page publique : nettoyer un éventuel returnTo périmé (évite de rediriger
  // l'accueil vers Login à tort).
  try {
    sessionStorage.removeItem('ccjaw.returnTo');
  } catch {
    /* ignore */
  }
  // Lien direct vers une fiche : on OUVRE la fiche. `linking.ts` mappe `mission/:missionId?`
  // sous `Main` en desktop web — un navigateur qui n'existe pas dans le stack PUBLIC. Le chemin
  // ne matchait donc rien et le visiteur atterrissait sur le catalogue, sa mission oubliée.
  // Le symptôme dépendait de la LARGEUR de la fenêtre (en mobile, l'écran est mappé à la racine
  // et le lien fonctionnait) : invisible tant qu'on ne teste qu'une taille d'écran.
  return missionIdToOpen() !== undefined ? 'MissionDetail' : 'PublicMissions';
}

function PublicNavigator() {
  return (
    <PublicStack.Navigator initialRouteName={computePublicInitialRoute()} screenOptions={{ headerShown: false, cardStyle: { flex: 1 } }}>
      <PublicStack.Screen name="PublicMissions" component={MissionsScreen} />
      {/* CCT-253 — la FICHE d'une mission fait partie de la vitrine : « Plus de détails » depuis
          le catalogue est le premier geste d'un visiteur. L'écran n'existant que dans le stack
          AUTHENTIFIÉ, react-navigation refusait la navigation en silence (« was not handled by
          any navigator », visible en console seulement) : le bouton ne faisait RIEN. */}
      <PublicStack.Screen name="MissionDetail" component={MissionDetailScreen}
        initialParams={{ missionId: missionIdToOpen() }} />
      <PublicStack.Screen name="Login" component={LoginScreen} />
      <PublicStack.Screen name="Invite" component={InviteScreen} initialParams={{ token: getPendingInvite() ?? undefined }} />
      <PublicStack.Screen name="Cart" component={CartScreen} options={{ presentation: 'modal' }} />
    </PublicStack.Navigator>
  );
}

// Loader plein écran (boot / retour d'authentification) : marque + message
// rassurant qui tourne + barre de progression animée (indéterminée). Remplace le
// spinner nu qui faisait « amateur » pendant l'attente Google/Entra.
// ⚠️ KEYS, not translated strings — this array is built at import time, before any hook has run.
const SPLASH_MESSAGES: MessageKey[] = ['boot.connecting', 'boot.preparing', 'boot.almost'];

function SplashScreen() {
  const t = useT();
  const { isDarkTheme } = useTheme();
  const colors = getThemeColors(isDarkTheme);
  const [idx, setIdx] = useState(0);
  const slide = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const rotate = setInterval(() => setIdx((i) => (i + 1) % SPLASH_MESSAGES.length), 2200);
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(slide, { toValue: 1, duration: 1100, useNativeDriver: false }),
        Animated.timing(slide, { toValue: 0, duration: 0, useNativeDriver: false }),
      ]),
    );
    loop.start();
    return () => { clearInterval(rotate); loop.stop(); };
  }, [slide]);

  const translateX = slide.interpolate({ inputRange: [0, 1], outputRange: [-70, 200] });

  return (
    <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: colors.background, padding: 24 }}>
      <Text style={{ fontSize: 22, fontWeight: '800', letterSpacing: 0.3, color: colors.textPrimary }}>CloudChrono</Text>
      <ActivityIndicator size="large" color={colors.primary} style={{ marginVertical: 20 }} />
      <Text style={{ fontSize: 15, color: colors.textSecondary, marginBottom: 16, textAlign: 'center' }}>
        {t(SPLASH_MESSAGES[idx])}
      </Text>
      <View style={{ width: 200, height: 4, borderRadius: 2, backgroundColor: colors.border, overflow: 'hidden' }}>
        <Animated.View style={{ width: 70, height: 4, borderRadius: 2, backgroundColor: colors.primary, transform: [{ translateX }] }} />
      </View>
    </View>
  );
}

export default function RootNavigator() {
  const { user, isLoading } = useAuth();
  // Capture le token d'invitation de l'URL AU PLUS TÔT (avant tout rendu de
  // navigateur susceptible de réécrire l'URL), pour les deux états d'auth.
  captureInvite();

  // ⚠️ THE UI DICTIONARY BOOTS HERE, AT THE ROOT — NOT IN A HOOK A SCREEN MAY OR MAY NOT USE.
  // It first lived inside `useUiLanguage`, which only the profile screen calls: `?lang=ar` on the
  // public home screen therefore did nothing at all, because nothing had asked for the language.
  // Measured, not guessed — the page rendered English with no `dir` attribute. Anything that must
  // hold for EVERY screen belongs where every screen is mounted.
  useEffect(() => { void bootUiLanguage(uiLanguageFromUrl()); }, []);

  return (
    <ThemeProvider>
      <PlayerProvider>
      <MissionsProvider>
      <NotificationsProvider>
      {/* Unread-update counters (CCT-348): below the notifications, whose refresh causes it
          shares — mount, return to the tab, push. No poll of its own. */}
      <UpdatesProvider>
      <StoryProgressProvider>
      <ChatStateProvider>
        <SafeAreaProvider>
          {isLoading
            ? <SplashScreen />
            : user
              ? <MainNavigator />
              : <PublicNavigator />
          }
          <GlobalLoadingIndicator />
          {/* Carte in-app à l'arrivée d'une notification (CCT-97), en plus du badge. */}
          <NotificationToast />
        </SafeAreaProvider>
      </ChatStateProvider>
      </StoryProgressProvider>
      </UpdatesProvider>
      </NotificationsProvider>
      </MissionsProvider>
      </PlayerProvider>
    </ThemeProvider>
  );
}

const styles = StyleSheet.create({
  tabIconContainer: {
    position: 'relative',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '100%',
    paddingVertical: 4,
  },
  badge: {
    position: 'absolute',
    right: -6,
    top: -3,
    borderRadius: 10,
    width: 20,
    height: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  badgeText: {
    fontSize: 12,
    fontWeight: 'bold',
  },
});


